import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { StaffMember } from '@/features/staff/types'
import type { Enrollment, Guardian, StudentSummary } from '@/features/students/types'

export interface PortalSubject {
  name: string
  code: string | null
  color: string | null
  isOptional: boolean
  weeklyPeriods?: number | null
}

export interface PortalCover {
  id: number
  startDate: string
  endDate: string
  coveringFor: string | null
}

/** A subject the teacher teaches in the section, and why: timetable, a cover, or their primary subject. */
export interface TeachingSubject {
  id: number
  name: string
  color: string | null
  source: 'timetable' | 'cover' | 'primary_subject'
  cover: PortalCover | null
}

/** A section the teacher works with today: as class teacher, a subject teacher or a substitute. */
export interface MySection {
  id: number
  name: string
  className: string
  sectionName: string
  room: string | null
  shift: string | null
  capacity: number | null
  studentCount: number
  isClassTeacher: boolean
  /** Covering the class teacher's duties today. */
  classCover: PortalCover | null
  /** Class teacher, or covered the section within the attendance edit window. */
  canTakeAttendance: boolean
  teaching: TeachingSubject[]
  subjects: PortalSubject[]
}

export interface StaffPortalData {
  profile: StaffMember
  sections: MySection[]
  academicYear: { id: number; name: string; startsOn: string; endsOn: string } | null
}

export interface RosterEntry {
  studentId: number
  studentCode: string
  fullName: string
  gender: string
  rollNumber: string | null
  photoUrl: string | null
  guardian: { name: string; phone: string | null } | null
}

export interface FamilyChild {
  student: StudentSummary
  bloodGroup: string | null
  guardians: Guardian[]
  enrollments: Enrollment[]
  classTeacher: { name: string; email: string | null; phone: string | null; photoUrl: string | null } | null
  room: string | null
  shift: { name: string; startTime: string | null; endTime: string | null } | null
  subjects: PortalSubject[]
}

export const staffPortalQuery = () =>
  queryOptions({ queryKey: ['portal', 'staff'], queryFn: () => api.get<StaffPortalData>('/me/staff') })
export const rosterQuery = (sectionId: number) =>
  queryOptions({
    queryKey: ['portal', 'staff', 'roster', sectionId],
    queryFn: () => api.get<RosterEntry[]>(`/me/staff/sections/${sectionId}/students`),
  })
export const familyQuery = () =>
  queryOptions({ queryKey: ['portal', 'family'], queryFn: () => api.get<FamilyChild[]>('/me/family') })
